import { Box, Divider, Group, Stack, createStyles } from '@mantine/core';
import type {
	GlossaryTermCreationProposal,
	SecodaAIProposalOut,
} from '@repo/api-codegen';
import { IconEmoji } from '@repo/common/components/IconEmoji/IconEmoji';
import MultiSelectorTarget from '@repo/common/components/MultiSelector/MultiSelectorTarget';
import { Text } from '@repo/foundations';
import { useMultiSelectorTargetSelected } from '../../../EntityModal/Metadata/RelatedEntitiesSelector/hooks.tsx';
import { RichEditor } from '../../../RichEditor';
import { OwnerRender, TeamRender } from '../../../TableV2/render';

const useStyles = createStyles((theme) => ({
	emojiContainer: {
		display: 'flex',
		alignItems: 'center',
		justifyContent: 'center',
		borderRadius: theme.radius.sm,
		background: theme.other.getColor('surface/secondary/default'),
		width: theme.spacing.xl,
		height: theme.spacing.xl,
	},
	markdownContainer: {
		backgroundColor: theme.other.getColor('surface/secondary/default'),
		border: `1px solid ${theme.other.getColor('border/secondary/default')}`,
		borderRadius: theme.radius.sm,
	},
	fieldTitle: {
		minWidth: 80,
	},
}));

export function GlossarySuggestionRenderer({
	data,
	proposal,
}: {
	proposal: SecodaAIProposalOut;
	data: GlossaryTermCreationProposal;
}) {
	const { classes, theme } = useStyles();

	const multiSelectorTargetSelected = useMultiSelectorTargetSelected(
		{},
		data.data.related_entity_ids || []
	);

	const hasRelatedEntities =
		data.data.related_entity_ids && data.data.related_entity_ids.length > 0;
	const hasOwner = !!data.data.owner_id;
	const hasTeam = !!data.data.team_id;

	return (
		<Stack spacing="md" p="xl">
			{/* Header */}
			<Box className={classes.emojiContainer}>
				<IconEmoji value={data.data.emoji || '📝'} size={28} />
			</Box>
			<Text size="xxl" weight="bold">
				{data.data.term}
			</Text>
			{/* Description */}
			{data.data.description && (
				<Box mt={theme.spacing.xs} mb={theme.spacing.sm}>
					<Text size="md">{data.data.description}</Text>
				</Box>
			)}

			{/* Meta Section */}
			<Box>
				<Stack spacing={0}>
					{hasOwner && (
						<Group spacing={0} align="center" noWrap>
							<Box className={classes.fieldTitle}>
								<Text size="sm" color="text/secondary/default">
									Owner
								</Text>
							</Box>
							<Box ml="lg">
								<OwnerRender
									record={{
										id: 'owner',
										integration: '',
										owners: data.data.owner_id ? [data.data.owner_id] : [],
										owners_groups: [],
									}}
								/>
							</Box>
						</Group>
					)}
					{hasTeam && (
						<Group spacing={0} align="center" noWrap>
							<Box className={classes.fieldTitle}>
								<Text size="sm" color="text/secondary/default">
									Teams
								</Text>
							</Box>
							<Box ml="md">
								<TeamRender
									record={{
										id: 'team',
										teams: data.data.team_id ? [data.data.team_id] : [],
									}}
								/>
							</Box>
						</Group>
					)}
					{hasRelatedEntities && (
						<Group spacing={0} align="center" noWrap>
							<Box className={classes.fieldTitle}>
								<Text size="sm" color="text/secondary/default">
									Related
								</Text>
							</Box>
							<Box ml="md">
								<MultiSelectorTarget
									selected={multiSelectorTargetSelected}
									iconType="react-node"
									property="entity"
									propertyLabel="resources"
									isMenuItemBadge={false}
									isViewerUser={false}
									permittedId={''}
									readOnly
									hideOnEmpty={true}
									truncate
								/>
							</Box>
						</Group>
					)}
				</Stack>
			</Box>

			<Divider my="sm" />

			{/* Supporting documentation */}
			<Text size="md" color="text/secondary/default" mb="xs">
				Supporting documentation
			</Text>
			<RichEditor value={data.data.full_definition || ''} readOnly />
		</Stack>
	);
}
